import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CloudArrowUp, CheckCircle, Calendar, Trash } from "@phosphor-icons/react";
import { fmtNumber } from "@/lib/format";

function SummaryStat({ label, value, accent, testId }) {
  return (
    <div className={`border p-3 ${accent || "border-canvas-line bg-canvas-card"}`} data-testid={testId}>
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">{label}</div>
      <div className="font-display text-xl font-bold mt-1 tabular-nums text-ink">{value}</div>
    </div>
  );
}

function BookingsDryRunReport({ data }) {
  if (!data) return null;
  const s = data.summary || {};
  return (
    <div className="space-y-3" data-testid="bookings-dry-run-report">
      <div className="border border-[#00B341] bg-[#00B341]/5 p-3 flex items-start gap-2">
        <CheckCircle size={16} weight="bold" className="text-[#00B341] shrink-0 mt-0.5" />
        <div className="text-sm">
          <span className="font-semibold">Parsed:</span>{" "}
          <span className="font-semibold">{fmtNumber(s.valid_bookings || 0)}</span> active bookings
          from {fmtNumber(s.total_rows || 0)} rows ·{" "}
          <span className="text-destructive">{fmtNumber(s.errors_count || 0)} skipped</span>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        <SummaryStat label="Active Bookings" value={fmtNumber(s.valid_bookings || 0)} testId="bookings-valid" />
        <SummaryStat label="Unique Customers" value={fmtNumber(s.unique_customers || 0)} testId="bookings-customers" />
        <SummaryStat label="Errors / Skipped" value={fmtNumber(s.errors_count || 0)} testId="bookings-errors" />
      </div>

      {(s.missing_branches || []).length > 0 && (
        <div className="border border-destructive/40 bg-destructive/5 p-3 text-sm">
          <div className="font-semibold text-destructive mb-1">
            ⚠ {s.missing_branches.length} branch(es) not found in MotorLedger:
          </div>
          <div className="font-mono text-xs">{s.missing_branches.join(" · ")}</div>
          <div className="text-ink-muted text-xs mt-1">
            Make sure your Sales migration ran first — branches are created during that step.
          </div>
        </div>
      )}
      {(s.missing_packages || []).length > 0 && (
        <div className="border border-destructive/40 bg-destructive/5 p-3 text-sm">
          <div className="font-semibold text-destructive mb-1">
            ⚠ {s.missing_packages.length} vehicle model(s) not found as Packages:
          </div>
          <div className="font-mono text-xs">{s.missing_packages.slice(0, 30).join(" · ")}</div>
          <div className="text-ink-muted text-xs mt-1">
            Packages are auto-created during the Sales migration. If a model only exists in
            bookings, create the package manually first.
          </div>
        </div>
      )}

      {(s.errors_preview || []).length > 0 && (
        <details className="border border-destructive/40 bg-destructive/5">
          <summary className="px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-destructive cursor-pointer">
            Errors preview ({s.errors_preview.length} of {s.errors_count})
          </summary>
          <table className="w-full text-xs">
            <thead className="bg-canvas-panel">
              <tr>
                <th className="text-left px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-ink-muted">Row</th>
                <th className="text-left px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-ink-muted">Reason</th>
              </tr>
            </thead>
            <tbody>
              {s.errors_preview.map((e, i) => (
                <tr key={i} className="border-t border-canvas-line">
                  <td className="px-3 py-1.5 font-mono">{e.row}</td>
                  <td className="px-3 py-1.5">{e.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </div>
  );
}

export default function BookingsMigrationPanel() {
  const [file, setFile] = useState(null);
  const [dryRun, setDryRun] = useState(null);
  const [committed, setCommitted] = useState(null);
  const [loading, setLoading] = useState(false);

  const upload = async (mode) => {
    if (!file) {
      toast.error("Select an Excel (.xlsx) file first.");
      return;
    }
    setLoading(true);
    setCommitted(null);
    if (mode === "dry-run") setDryRun(null);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("mode", mode);
      const { data } = await api.post("/migration/excel-bookings", form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });
      if (mode === "dry-run") {
        setDryRun(data);
        toast.success(`Parsed ${data.summary?.valid_bookings || 0} bookings — review and commit`);
      } else {
        setCommitted(data);
        toast.success("Bookings committed successfully");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border-2 border-ink" data-testid="bookings-migration-panel">
      <div className="px-4 py-3 border-b border-canvas-line flex items-center gap-2">
        <Calendar size={18} weight="bold" className="text-ink" />
        <h3 className="font-display text-base font-semibold">
          Step 3 — Import active bookings from Excel
        </h3>
      </div>
      <div className="p-4 space-y-4 text-sm">
        <div>
          <p>
            Expects a sheet with columns: <code>Date</code>, <code>Branch Name</code>,{" "}
            <code>Customer Name</code>, <code>Phone Number</code>, <code>Vehicle Model</code>,{" "}
            <code>Amount</code>, <code>Status</code>, <code>Colour</code>.
          </p>
          <p className="text-ink-muted mt-2">
            Only <strong>Active</strong> bookings are imported (non-active rows are skipped). Each
            becomes a package-based booking with a single cash payment line of the listed Amount.
            <br />
            <strong>Run the sales migration first</strong> — branches & packages need to exist
            before bookings can be linked.
            <br />
            <strong className="text-destructive">Strict integrity:</strong> if any row references a
            branch or package that doesn't exist (or whose package is inactive), the entire file is
            rejected. Seed the missing entries first, then re-upload.
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <label className="px-3 py-2 border-2 border-dashed border-canvas-line cursor-pointer hover:border-ink inline-flex items-center gap-2">
            <CloudArrowUp size={14} weight="bold" />
            <span className="text-xs font-semibold">
              {file ? file.name : "Choose .xlsx file…"}
            </span>
            <input
              type="file"
              accept=".xlsx,.xlsm"
              onChange={(e) => {
                setFile(e.target.files?.[0] || null);
                setDryRun(null);
                setCommitted(null);
              }}
              data-testid="bookings-file-input"
              className="hidden"
            />
          </label>
          <button
            onClick={() => upload("dry-run")}
            disabled={!file || loading}
            data-testid="bookings-dry-run-btn"
            className="px-3 py-2 border border-ink text-ink text-xs font-semibold hover:bg-ink hover:text-white disabled:opacity-50"
          >
            {loading ? "Parsing…" : "Run Dry-Run"}
          </button>
          <button
            onClick={() => upload("commit")}
            disabled={!file || !dryRun || loading}
            data-testid="bookings-commit-btn"
            className="px-3 py-2 bg-ink text-white text-xs font-semibold hover:bg-brand disabled:opacity-50"
            title={!dryRun ? "Run dry-run first" : "Commit to DB"}
          >
            {loading ? "Committing…" : "Commit to Database"}
          </button>
        </div>

        <BookingsDryRunReport data={dryRun} />

        <WipeBookingsBlock />

        {committed && (
          <div className="border border-[#00B341] bg-[#00B341]/5 p-4" data-testid="bookings-commit-report">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle size={18} weight="bold" className="text-[#00B341]" />
              <h4 className="font-display text-base font-semibold">Bookings committed</h4>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
              {Object.entries(committed.created || {}).map(([k, v]) => (
                <div key={k} className="border border-canvas-line bg-canvas-card p-2">
                  <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">{k}</div>
                  <div className="font-display text-lg font-bold tabular-nums">{fmtNumber(v)}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function WipeBookingsBlock() {
  const [show, setShow] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const wipe = async () => {
    if (confirm !== "WIPE-ALL-BOOKINGS") {
      toast.error("Type exactly 'WIPE-ALL-BOOKINGS' to proceed.");
      return;
    }
    if (!window.confirm("This will delete ALL bookings. Sales/vehicles/customers are kept. Continue?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/migration/wipe-bookings-only", { confirm });
      setResult(data.deleted);
      setShow(false);
      setConfirm("");
      const total = Object.values(data.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`Wiped ${total} records across ${Object.keys(data.deleted || {}).length} collections`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-destructive/40 bg-destructive/5 p-3" data-testid="wipe-bookings-block">
      <div className="flex items-start gap-2 mb-2">
        <Trash size={14} weight="bold" className="text-destructive shrink-0 mt-0.5" />
        <div className="text-sm">
          <div className="font-semibold text-destructive">Wipe existing bookings before re-importing</div>
          <p className="text-ink-muted text-xs mt-1">
            Deletes ALL bookings (active, delivered, cancelled) and their advance-payment vouchers.
            Sales, vehicles, customers are kept; any sale linked to a wiped booking has its{" "}
            <code>booking_id</code> cleared but stays intact.
          </p>
        </div>
      </div>
      {!show ? (
        <button
          type="button"
          onClick={() => setShow(true)}
          data-testid="open-wipe-bookings"
          className="px-3 py-1.5 border border-destructive text-destructive text-xs font-semibold hover:bg-destructive hover:text-white"
        >
          Reveal wipe action
        </button>
      ) : (
        <div className="space-y-2">
          <div className="text-xs">Type <code className="font-bold">WIPE-ALL-BOOKINGS</code> to confirm:</div>
          <input
            type="text"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="WIPE-ALL-BOOKINGS"
            data-testid="wipe-bookings-confirm-input"
            className="px-2 py-1.5 border border-canvas-line bg-white text-sm font-mono w-full max-w-xs"
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={wipe}
              disabled={busy || confirm !== "WIPE-ALL-BOOKINGS"}
              data-testid="confirm-wipe-bookings"
              className="px-3 py-1.5 bg-destructive text-white text-xs font-semibold hover:opacity-90 disabled:opacity-50"
            >
              {busy ? "Wiping…" : "Wipe Bookings Now"}
            </button>
            <button
              type="button"
              onClick={() => { setShow(false); setConfirm(""); }}
              className="px-3 py-1.5 border border-canvas-line text-xs"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {result && (
        <div className="mt-2 text-xs font-mono text-ink-muted" data-testid="wipe-bookings-result">
          {Object.entries(result).map(([k, v]) => `${k}=${v}`).join(" · ")}
        </div>
      )}
    </div>
  );
}
